import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Popover, Card, Button, Row, Col, Form, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { ApplicationState } from '../../../reducers';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import { Translation } from '../../../../types/application';
import { hasPermission } from '../../../utils/Permissions';
import ModalTranslationTable from './ModalTranslationTable';
import { fetchTranslations } from '../../../actions/parent/language/fetch';
import { typingDone } from '../../../utils/Utils';
import { AsyncDispatch } from '../../../../types/global';
import { LanguageTranslation } from '../../../../types/language';
import { useAppDispatch } from '../../../store/ReduxStore';

type QualifierPopoverProps = {
  children: React.ReactNode;
  type?: string;
  value: string;
  metaUomId?: number;
  valueTranslations: Translation[];
  translationTypeId: number;
  initialVisible?: boolean;
  handleChange: ({ value, metaUomId }: { value: string; metaUomId?: number }) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

const QualifierPopover: React.FC<QualifierPopoverProps> = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AsyncDispatch = useAppDispatch();

  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const [translations, setTranslations] = React.useState(props.valueTranslations || []);

  const { qualifierMetaUoms, user } = useSelector(
    (state: ApplicationState) => ({
      qualifierMetaUoms: state.resources.data.application.qualifier_meta_uoms,
      user: state.user.user,
    }),
    shallowEqual
  );

  const canManageTranslations = hasPermission(user, 'can_manage_translation');

  const validationSchema = Yup.object().shape({
    value:
      props.type === 'num'
        ? Yup.string()
            .required(t('validation:required'))
            .matches(
              /(^\d+-\d+\/\d+$|^\d+\/\d+$|^\d+[.]\d+$|^\d+$)/g,
              t('validation:invalidNumber')
            )
        : Yup.string().required(t('validation:required')),
  });

  const formMethods = useForm<{ value: string; metaUomId?: number }>({
    defaultValues: {
      value: props.value || '',
      metaUomId: props.metaUomId || undefined,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, formState, reset } = formMethods;
  const { isDirty, isSubmitting } = formState;

  React.useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('qualifierValueInput');
      if (popoverVisible && input) input.focus();
    }, 100);
  }, [popoverVisible]);

  React.useEffect(() => {
    if (props.initialVisible) setPopoverVisible(true);
  }, [props.initialVisible]);

  React.useEffect(() => {
    if (!popoverVisible) reset({ value: props.value || '', metaUomId: props.metaUomId });
  }, [popoverVisible, props.metaUomId, props.value, reset]);

  const handleVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
    if (visible && props.onOpen) props.onOpen();
    else if (props.onClose) props.onClose();
  };

  const onSubmit = (values: { value: string; metaUomId?: number }) => {
    props.handleChange(values);
    handleVisibleChange(false);
  };

  const handleChangedValue = (value: string) => {
    setValue('value', value, { shouldDirty: true, shouldValidate: true });
    typingDone(() =>
      dispatch(
        fetchTranslations({
          translationTypeId: props.translationTypeId,
          keywords: value,
        })
      ).then(response => {
        const translation = response.value.data.find((data: LanguageTranslation) =>
          data.translations?.find(t => t.language_id === 1 && t.value === value)
        );
        setTranslations(translation?.translations || []);
      })
    );
  };

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      overlayClassName="help application_listing__popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        <Card className="qualifier__value-popover" bordered={false}>
          <FormProvider {...formMethods}>
            <Form
              layout="vertical"
              className="application__defaults-form"
              onFinish={handleSubmit(onSubmit)}
            >
              <Row gutter={20} align="top">
                <Col span={props.type !== 'note' ? 12 : 24}>
                  <FormInput
                    id="qualifierValueInput"
                    name="value"
                    label={t('application:qualifier.valueLabel')}
                    required
                    handleChange={e => handleChangedValue(e.target.value || '')}
                    useHookForm
                  />
                </Col>
                {props.type !== 'note' && (
                  <Col span={12}>
                    <FormSelect
                      name="metaUomId"
                      values={qualifierMetaUoms}
                      label={t('common:unit')}
                      allowClear
                      showSearch
                      // todo: disable
                      useHookForm
                    />
                  </Col>
                )}
              </Row>
              {canManageTranslations && (
                <React.Fragment>
                  <div className="flex justify-between">
                    <div>{t('language:translations')}</div>
                    <Popconfirm
                      title={t('application:confirmSwitchToTanslations')}
                      okText={t('common:yes')}
                      cancelText={t('common:cancel')}
                      onConfirm={() =>
                        navigate('/settings/translations/translation-settings', {
                          state: { segmentId: 4 },
                        })
                      }
                    >
                      <Button size="small" className="truncate">
                        {t('language:editTranslations')}
                      </Button>
                    </Popconfirm>
                  </div>
                  <div className="popover-modal-translation-table">
                    <ModalTranslationTable valueTranslations={translations || []} />
                  </div>
                </React.Fragment>
              )}

              <div className="flex-col items-end">
                <div>
                  <Button
                    onClick={() => handleVisibleChange(false)}
                    className="drawer-submit__bottom-cancel"
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button type="primary" htmlType="submit" disabled={!isDirty || isSubmitting}>
                    {t('common:ok')}
                  </Button>
                </div>
              </div>
            </Form>
          </FormProvider>
        </Card>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default QualifierPopover;
